/**
 * BootstrapLoadFlow.jsx
 *
 * data-fit LLC — DecisionFlow
 * Stage 1 LOAD — Parent Flow Component
 * Branch: feature/compliance-lifecycle-foundation
 *
 * Owns all shared state across the LOAD-01 → LOAD-02 → LOAD-03 journey.
 * Sole owner of bootstrapBatchId, validationResult, aiResponse, aiStatus, reloadMode.
 * All screen transitions managed here. Children receive state as props and
 * emit changes via callback props only — no child declares aiResponse or aiStatus
 * as local state.
 *
 * Fix applied (F1): onAiResponseReceived passed to PostLoadSummary so Scope
 * Advisor response updates parent aiResponse and renders in ScopeAdvisorPanel.
 * Fix applied (F2): onAiStatusChange + onAiResponseReceived passed to
 * ValidationLoadReport so re-upload AI call updates parent state correctly.
 *
 * Spec authority: ComponentSpec_LOAD_corrected_S86g.md
 * Path: ui/src/components/tenant-admin/BootstrapLoadFlow.jsx
 * M040 C2: hardcoded bg/text → tokens
 */

import React, { useCallback, useState } from 'react';
import BootstrapUpload from './BootstrapUpload';
import ValidationLoadReport from './ValidationLoadReport';
import PostLoadSummary from './PostLoadSummary';

const SCREEN_LOAD_01 = 'LOAD-01';
const SCREEN_LOAD_02 = 'LOAD-02';
const SCREEN_LOAD_03 = 'LOAD-03';

export default function BootstrapLoadFlow({ tenantId, tier }) {
  const [currentScreen, setCurrentScreen] = useState(SCREEN_LOAD_01);
  const [bootstrapBatchId, setBootstrapBatchId] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [aiResponse, setAiResponse] = useState(null);
  const [aiStatus, setAiStatus] = useState('idle');
  const [reloadMode, setReloadMode] = useState(false);

  // ── LOAD-01 callbacks ────────────────────────────────────────────────────

  const handleValidationComplete = useCallback((result) => {
    setBootstrapBatchId(result?.bootstrapBatchId || result?.bootstrap_batch_id || null);
    setValidationResult(result);
    setCurrentScreen(SCREEN_LOAD_02);
  }, []);

  const handleAiStatusChange = useCallback((status) => {
    setAiStatus(status);
  }, []);

  const handleAiResponseReceived = useCallback((response) => {
    setAiResponse(response);
    setAiStatus('ready');
  }, []);

  // ── LOAD-02 callbacks ────────────────────────────────────────────────────

  const handleLoadConfirmed = useCallback((batchId) => {
    setBootstrapBatchId(batchId);
    setAiResponse(null);
    setAiStatus('idle');
    setCurrentScreen(SCREEN_LOAD_03);
  }, []);

  const handleBackToUpload = useCallback(() => {
    setCurrentScreen(SCREEN_LOAD_01);
  }, []);

  const handleNewBatchId = useCallback((batchId) => {
    setBootstrapBatchId(batchId);
    setValidationResult(null);
    setAiResponse(null);
    setAiStatus('loading');
  }, []);

  // ── LOAD-03 callbacks ────────────────────────────────────────────────────

  const handleProceedToConfigure = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('decisionflow:navigate', {
        detail: { target: 'CONFIGURE', source: SCREEN_LOAD_03, bootstrapBatchId },
      })
    );
  }, [bootstrapBatchId]);

  const handleBackToReload = useCallback(() => {
    setReloadMode(true);
    setBootstrapBatchId(null);
    setValidationResult(null);
    setAiResponse(null);
    setAiStatus('idle');
    setCurrentScreen(SCREEN_LOAD_01);
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <section className="min-h-screen bg-[rgb(var(--color-bg))] text-[rgb(var(--color-text))]">
      {currentScreen === SCREEN_LOAD_01 && (
        <BootstrapUpload
          tenantId={tenantId}
          tier={tier}
          reloadMode={reloadMode}
          aiStatus={aiStatus}
          onValidationComplete={handleValidationComplete}
          onAiStatusChange={handleAiStatusChange}
          onAiResponseReceived={handleAiResponseReceived}
        />
      )}

      {currentScreen === SCREEN_LOAD_02 && (
        <ValidationLoadReport
          bootstrapBatchId={bootstrapBatchId}
          validationResult={validationResult}
          aiResponse={aiResponse}
          aiStatus={aiStatus}
          tier={tier}
          onLoadConfirmed={handleLoadConfirmed}
          onBackToUpload={handleBackToUpload}
          onNewBatchId={handleNewBatchId}
          onAiStatusChange={handleAiStatusChange}
          onAiResponseReceived={handleAiResponseReceived}
        />
      )}

      {currentScreen === SCREEN_LOAD_03 && (
        <PostLoadSummary
          bootstrapBatchId={bootstrapBatchId}
          tier={tier}
          aiResponse={aiResponse}
          aiStatus={aiStatus}
          onAiStatusChange={handleAiStatusChange}
          onAiResponseReceived={handleAiResponseReceived}
          onProceedToConfigure={handleProceedToConfigure}
          onBackToReload={handleBackToReload}
        />
      )}
    </section>
  );
}
